/**
 * The visual pieces wall cards share (calm wall mockup 2026-10-19): a
 * gadget's own paint, a soft tint block derived from a seed, a calm dark code
 * card, a date accent and coloured event rows. Sizes are in em, so they grow
 * with the wall's type. Content stays solid; the card container owns the
 * radius and shadow.
 */
import type { CSSProperties, ReactNode } from "react";
import type { CardPreviewDensity } from "@/components/shapes/card-previews";
import { SurfaceFade } from "@/components/ui";
import { cn } from "@/lib/cn";
import { cardTintStyle } from "./card-tint";

/** A regular gadget card's height. */
const GADGET_CLASS: Record<CardPreviewDensity, string> = {
  regular: "h-[15em]",
  compact: "h-full",
};

/** The top light every painted card wears (`--card-highlight`). */
const HIGHLIGHT = "var(--card-highlight)";

/**
 * A gadget in its own colour (`--gadget-*` in index.css), white ink. Without
 * `paint` its frame paints it (`ObjectFrame`), so an opened window shows one
 * paint, never a block on a block.
 */
export function GadgetCard({
  paint,
  density,
  className,
  children,
  "data-testid": testId,
}: {
  /** A `--gadget-*` token, e.g. `var(--gadget-music)`. */
  paint?: string;
  density: CardPreviewDensity;
  className?: string;
  children: ReactNode;
  "data-testid"?: string;
}) {
  return (
    <div
      className={cn(
        "flex min-h-0 flex-col justify-between gap-[0.45em] overflow-hidden p-[1.4em] text-[color:var(--gadget-ink)]",
        GADGET_CLASS[density],
        className,
      )}
      style={{ background: paint, boxShadow: HIGHLIGHT }}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

/** A soft field (landing artwork): tint b glows top right over a 160° ramp. */
const TINT_PAINT: CSSProperties = {
  backgroundImage:
    "radial-gradient(90% 80% at 85% 15%, var(--tint-b), transparent 65%), linear-gradient(160deg, var(--tint-a), var(--tint-b))",
  boxShadow: HIGHLIGHT,
};

/** A soft gradient in the tint `seed` wears, ink that reads on it. */
export function TintBlock({
  seed,
  className,
  style,
  children,
  "data-testid": testId,
}: {
  seed: string;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  "data-testid"?: string;
}) {
  return (
    <div
      className={cn("text-[color:var(--card-tint-ink)]", className)}
      style={{ ...cardTintStyle(seed), ...TINT_PAINT, ...style }}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

/**
 * Where a code window sits: a regular or compact card, or a frame's body
 * (`frame`), which fills the frame and never fades; the frame decides
 * whether it scrolls (the wall's cards never do).
 */
export type CodeWindowFit = CardPreviewDensity | "frame";

const CODE_WINDOW_FIT: Record<CodeWindowFit, string> = {
  regular: "max-h-[20em] overflow-hidden",
  compact: "h-full overflow-hidden",
  // A window painted as code (`data-paint`, Part 10) is the paint itself,
  // so the body draws no second background.
  frame: "h-full [[data-paint=code]_&]:bg-transparent",
};

/**
 * A calm dark code card (mockup `.term`): mono lines on the code paint, no
 * window header; the first line is the caller's muted label line.
 */
export function CodeWindow({
  density,
  className,
  children,
  "data-testid": testId,
}: {
  density: CodeWindowFit;
  className?: string;
  children: ReactNode;
  "data-testid"?: string;
}) {
  return (
    <div
      className={cn(
        "relative min-h-0 bg-[var(--gadget-code)] px-[1.55em] py-[1.5em] font-mono text-[0.8em] leading-[1.7] text-[color:var(--gadget-code-ink)] [--surface-fade:var(--gadget-code)]",
        CODE_WINDOW_FIT[density],
        className,
      )}
      data-testid={testId}
    >
      {children}
      {/* Long code fades into the code paint, never the card's paper. */}
      {density !== "frame" && (
        <SurfaceFade at={density === "compact" ? undefined : "20em"} />
      )}
    </div>
  );
}

/** A day number with its weekday in the date accent (agenda, calendar). */
export function DayAccent({
  day,
  weekday,
  label,
}: {
  day: string | null;
  weekday: string | null;
  /** Shown instead when the date has no day number. */
  label: string | null;
}) {
  if (!day) {
    return (
      <p className="truncate text-[0.86em] font-semibold text-[color:var(--gadget-date)]">
        {label}
      </p>
    );
  }
  return (
    <p className="flex items-baseline gap-[0.5em] text-[0.86em] font-semibold uppercase text-[color:var(--gadget-date)]">
      <span className="font-display text-[2.2em] font-medium leading-none text-card-ink">
        {day}
      </span>
      {weekday}
    </p>
  );
}

const EVENT_COLORS = [
  "var(--gadget-event-1)",
  "var(--gadget-event-2)",
  "var(--gadget-event-3)",
];

export interface CardEventRow {
  id: string;
  title: string;
  time: string;
  /** Shown under the time by `EventRows` with `details` (the opened frame). */
  detail?: string;
  /** The event's own colour when the content names one. */
  color?: string;
  current?: boolean;
}

/**
 * Events as rows with a colour dot, the title over its time and, with
 * `details`, each row's detail.
 */
export function EventRows({
  rows,
  details = false,
  testId,
}: {
  rows: readonly CardEventRow[];
  details?: boolean;
  /** Each row's test id is `${testId}-${row.id}`. */
  testId?: string;
}) {
  return (
    <div className="grid min-h-0 gap-[0.8em] overflow-hidden">
      {rows.map((row, index) => (
        <div
          key={row.id}
          className="grid grid-cols-[8px_1fr] gap-[0.7em] leading-[1.3]"
          data-testid={testId && `${testId}-${row.id}`}
        >
          <i
            aria-hidden="true"
            className="mt-[0.42em] h-2 w-2 rounded-full"
            style={{
              background:
                row.color ?? EVENT_COLORS[index % EVENT_COLORS.length],
            }}
          />
          <span className="min-w-0">
            <span
              className={cn(
                "block truncate text-card-ink",
                row.current && "font-semibold",
              )}
            >
              {row.title}
            </span>
            {row.time && <EventMeta>{row.time}</EventMeta>}
            {details && row.detail && <EventMeta>{row.detail}</EventMeta>}
          </span>
        </div>
      ))}
    </div>
  );
}

/** A muted line under an event's title. */
function EventMeta({ children }: { children: ReactNode }) {
  return (
    <span className="mt-[0.15em] block truncate text-[0.84em] text-card-muted">
      {children}
    </span>
  );
}
